import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, ZoomControl } from "react-leaflet";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { getHereTileUrl } from "../../helpers/hereTileHelper";
import { ControlsPanel } from "./ControlsPanel";
import { RemoveDefaultZoom, InvalidateOn, ChooseViewControl } from "./MapChrome";
import { InstallBaseLayers } from "./BaseLayers";
import { AutoFitBounds } from "./AutoFitBounds";
import { useDelayBuckets } from "./hooks/useDelayBuckets";
import { ClusterLayer } from "./ClusterLayer";
import "./hereMapsStyle.scss";

type Props = { PositionList: SimplifiedCsdsListInfo[]; apiKey?: string };

const suffixUrl = process.env.REACT_APP_CSTT_DOMAIN?.trim() ?? "";
const suffix = suffixUrl && !suffixUrl.startsWith(".") ? `.${suffixUrl}` : suffixUrl;

const EQUIP_BASE: string = `https://equipment${suffix}/communication/`;


const PositionDelayMap: React.FC<Props> = ({ PositionList, apiKey }) => {
  const layersRef = useRef<L.Control.Layers | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const gearBtnRef = useRef<HTMLButtonElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [controlsOpen, setControlsOpen] = useState(false);

  // delay/bucket filtering
  const { selectedDelayBase, setSelectedDelayBase, visible, setVisible, bucketOf, filtered } =
    useDelayBuckets(PositionList, 100);

  useEffect(() => {
    const p = panelRef.current;
    const g = gearBtnRef.current;
    if (p) { L.DomEvent.disableClickPropagation(p); L.DomEvent.disableScrollPropagation(p); }
    if (g) { L.DomEvent.disableClickPropagation(g); L.DomEvent.on(g, "wheel", L.DomEvent.stop); }
    return () => { if (g) L.DomEvent.off(g, "wheel", L.DomEvent.stop); };
  }, []);

  useEffect(() => { mapRef.current?.invalidateSize(); }, [controlsOpen]);

  const urls = useMemo(() => {
    const key = apiKey || (process.env.REACT_APP_HERE_MAPS_API_KEY as string);
    const size = 256; const LANG = "en";
    return {
      day: getHereTileUrl({ apiKey: key, style: "explore.day", size, lang: LANG }),
      sat: getHereTileUrl({ apiKey: key, style: "satellite.day", size, format: "png8", lang: LANG }),
      hybrid: getHereTileUrl({ apiKey: key, style: "explore.satellite.day", size, format: "png8", lang: LANG }),
    };
  }, [apiKey]);

  return (
    <div className="map-outer">
      <div className="map-container">
        <div className="map-fabs">
          <button
            type="button"
            ref={gearBtnRef}
            className="map-settings-btn"
            onClick={(e) => { e.stopPropagation(); setControlsOpen(v => !v); }}
            aria-controls="map-controls"
            aria-expanded={controlsOpen}
            title="Map settings"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M19.14,12.94a7.43,7.43,0,0,0,.05-.94,7.43,7.43,0,0,0-.05-.94l2.11-1.65a.49.49,0,0,0,.12-.63l-2-3.46a.5.5,0,0,0-.6-.22l-2.49,1a7.31,7.31,0,0,0-1.62-.94l-.38-2.65A.5.5,0,0,0,13.21,1H10.79a.5.5,0,0,0-.49.42L9.92,4.07a7.31,7.31,0,0,0-1.62.94l-2.49-1a.5.5,0,0,0-.6.22l-2,3.46a.49.49,0,0,0,.12.63L5.43,11.06a7.43,7.43,0,0,0-.05.94,7.43,7.43,0,0,0,.05.94L3.32,14.59a.49.49,0,0,0,.12.63l2,3.46a.5.5,0,0,0,.6.22l2.49-1a7.31,7.31,0,0,0,1.62.94l.38,2.65a.5.5,0,0,0,.49.42h2.42a.5.5,0,0,0,.49-.42l.38-2.65a7.31,7.31,0,0,0,1.62-.94l2.49,1a.5.5,0,0,0,.6-.22l2-3.46a.49.49,0,0,0,.12-.63Zm-7.14,2.56A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
            </svg>
          </button>
        </div>

        <ControlsPanel
          open={controlsOpen}
          onClose={() => setControlsOpen(false)}
          selectedDelayBase={selectedDelayBase}
          setSelectedDelayBase={setSelectedDelayBase}
          visible={visible}
          setVisible={setVisible}
          panelRef={panelRef}
        />

        <MapContainer
          center={[62, 15]}
          zoom={4}
          minZoom={3}
          maxZoom={18}
          preferCanvas
          zoomControl={false}
          style={{ width: "100%", height: "100%" }}
          ref={mapRef}
          whenReady={() => requestAnimationFrame(() => mapRef.current?.invalidateSize())}
          maxBounds={[[-85, -180], [85, 180]]}
          maxBoundsViscosity={1.0}   // nice elastic clamp
          worldCopyJump={false}
        >
          <RemoveDefaultZoom />
          <ZoomControl position="bottomright" />
          <InvalidateOn deps={[controlsOpen]} />

          <InstallBaseLayers urls={urls} onReady={(c) => { layersRef.current = c; }} />

          <ChooseViewControl onClick={() => {
            const ctrl = layersRef.current;
            if (!ctrl) return;
            const el = (ctrl as any).getContainer?.();
            const isOpen = !!el && el.classList.contains("leaflet-control-layers-expanded");
            isOpen ? ctrl.collapse() : ctrl.expand();
          }} />

          <ClusterLayer
            points={filtered}
            bucketOf={bucketOf}
            disableAt={13}
            equipmentBaseUrl={EQUIP_BASE!}
          />

          <AutoFitBounds points={filtered} />
        </MapContainer>
      </div>
    </div>
  );
};

export default PositionDelayMap;
